import { ref, type Ref } from 'vue'
import { sdk } from '@/graphql/client'
import { prepareArgs, type StatsFilters } from '@/components/domain/statistics/statistics-filters'
import { type AdminStatisticsGetQueryVariables } from '@/graphql/generated/sdk'

export interface DeviceStats {
    id: number
    username: string
    name: string
    values: number[]
    count: string
}

export interface TicketsetSumSub {
    id: number
    name: string
    count: number
    type: string
}

export interface TicketsetSum {
    id: number
    total: number
    used: number
    real: number
    realNodevice: number
    eventName: string
    name: string
    sub: TicketsetSumSub[]
}

export interface StatsResult {
    ticketsTotal: number
    ticketsValidated: number
    labels: string[]
    device: DeviceStats[]
    ticketsetSums: TicketsetSum[]
    ticketsetSumsTotal: TicketsetSum | null
}

export function useStatisticsData() {
    const stats: Ref<StatsResult | null> = ref(null)
    const loading = ref(false)
    const error = ref<string | null>(null)

    async function fetch(filters: StatsFilters) {
        loading.value = true
        error.value = null
        try {
            const args = prepareArgs(filters)
            const r = await sdk.AdminStatisticsGet(args as AdminStatisticsGetQueryVariables)
            const raw = r.admin?.statistics?.get
            if (!raw) {
                stats.value = null
                return
            }
            const rawDevice = (raw.device ?? []) as DeviceStats[]
            // Drop deprecated checkpoint records (empty username).
            const device = rawDevice.filter(d => d.username !== '')
            stats.value = {
                ticketsTotal: raw.ticketsTotal ?? 0,
                ticketsValidated: raw.ticketsValidated ?? 0,
                labels: (raw.labels ?? []).filter((l): l is string => l !== null),
                device,
                ticketsetSums: (raw.ticketsetSums ?? []) as TicketsetSum[],
                ticketsetSumsTotal: (raw.ticketsetSumsTotal ?? null) as TicketsetSum | null
            }
        } catch (e) {
            error.value = (e as Error).message
            stats.value = null
        } finally {
            loading.value = false
        }
    }

    return { stats, loading, error, fetch }
}
